import { FormBuilder, FormGroup } from '@angular/forms';
import {
    MyElCertificazioniPassateEntity,
    MyElContrattoSocietaCertificazione,
    MyElCopiaBilancioAllegato,
    MyElEnteErogatore,
    MyElNotaIntegrativaAllegata,
    MyElRelazCertifAllegata,
    MyElSocietaPartecipate,
    MyVerbale,
} from '../../../../models/interfaces/revisione.interface';
import { rebuildFormArray } from '@shared/helpers/form.helper';
import { formPaths } from '@shared/constants/form-paths.const';
import { CoopValidators } from '@core/helpers/validators.helper';

export const buildSocietaPartecipateFormArray = (fb: FormBuilder, myVerbale: MyVerbale, form: FormGroup, withValidators = true) => {
    const societaPartecipate = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_SocietaPartecipate.my_ElSocietaPartecipate;
    rebuildFormArray(fb, societaPartecipate, form, `${formPaths.bilancio}.my_SocietaPartecipate.my_ElSocietaPartecipate`, (fbInner, item) =>
        buildSocietaPartecipataGroup(fbInner, item, withValidators)
    );
};

export const buildSocietaPartecipataGroup = (fb: FormBuilder, item?: MyElSocietaPartecipate, withValidators = true): FormGroup => {
    return fb.group({
        my_SocPartec: withValidators ? [item?.my_SocPartec ?? '', CoopValidators.required] : item?.my_SocPartec ?? '',
        my_CodiceFiscaleConsPartecipata: item?.my_CodiceFiscaleConsPartecipata,
        my_TipoSocietaPartec: withValidators ? [item?.my_TipoSocietaPartec ?? '', CoopValidators.required] : item?.my_TipoSocietaPartec ?? '',
        my_ImpCapSott: withValidators ? [item?.my_ImpCapSott ?? '', CoopValidators.required] : item?.my_ImpCapSott ?? '',
        my_PercSpaSrl: item?.my_PercSpaSrl,
        my_SpecTipoSoc: item?.my_SpecTipoSoc ?? '',
    });
};

export const buildEntiErogatoreFormArray = (fb: FormBuilder, myVerbale: MyVerbale, form: FormGroup) => {
    const entiErogatore = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_EntiErogatori.my_ElEnteErogatore;
    rebuildFormArray(fb, entiErogatore, form, `${formPaths.bilancio}.my_EntiErogatori.my_ElEnteErogatore`, (fbInner, item) => buildEnteErogatoreGroup(fbInner, item));
};

export const buildEnteErogatoreGroup = (fb: FormBuilder, item?: MyElEnteErogatore): FormGroup => {
    // my_SpecificaTipologia is enabled only when the tipologia is "6" (Altro: specificare)
    // Required-ness for my_Tipologia/my_Ente/my_Importo/my_SpecificaTipologia is conditional on
    // my_CkContribPubb (dati-bilancio-contributi-pubblici.rules.ts); no static validator here.
    const specificaTipologia = { value: item?.my_SpecificaTipologia ?? '', disabled: item?.my_Tipologia !== '6' };
    return fb.group({
        my_Tipologia: item?.my_Tipologia ?? '',
        my_Ente: item?.my_Ente ?? '',
        my_Importo: item?.my_Importo ?? '',
        my_SpecificaTipologia: specificaTipologia,
    });
};

export const buildRelazCertifAllegataFormArray = (fb: FormBuilder, myVerbale: MyVerbale, form: FormGroup, withValidators = true) => {
    const relazCertifAllegata = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_RelazCertifAllegata.my_ElRelazCertifAllegata;
    rebuildFormArray(fb, relazCertifAllegata, form, `${formPaths.bilancio}.my_RelazCertifAllegata.my_ElRelazCertifAllegata`, (fbInner, item) =>
        buildRelazCertifAllegataGroup(fbInner, item, withValidators)
    );
};

export const buildRelazCertifAllegataGroup = (fb: FormBuilder, item?: MyElRelazCertifAllegata, withValidators = true): FormGroup => {
    return fb.group({
        my_RelazioneCertificazione: withValidators ? [item?.my_RelazioneCertificazione ?? '', CoopValidators.required] : item?.my_RelazioneCertificazione ?? '',
    });
};

export const buildContrattoSocCertificazioneFormArray = (fb: FormBuilder, myVerbale: MyVerbale, form: FormGroup, withValidators = true) => {
    const contrattoSocCertificazione = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_ContrattoSocietaCertificazione.my_ElContrattoSocietaCertificazione;
    rebuildFormArray(
        fb,
        contrattoSocCertificazione,
        form,
        `${formPaths.bilancio}.my_ContrattoSocietaCertificazione.my_ElContrattoSocietaCertificazione`,
        (fbInner, item) => buildContrattoSocCertificazioneGroup(fbInner, item, withValidators)
    );
};

export const buildContrattoSocCertificazioneGroup = (fb: FormBuilder, item?: MyElContrattoSocietaCertificazione, withValidators = true): FormGroup => {
    return fb.group({
        my_ContrattoCertificazione: withValidators ? [item?.my_ContrattoCertificazione, CoopValidators.required] : item?.my_ContrattoCertificazione,
    });
};

export const buildNotaIntegrativaAllegataFormArray = (fb: FormBuilder, myVerbale: MyVerbale, form: FormGroup) => {
    const notaIntegrativaAllegata = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_NotaIntegrativaAllegata.my_ElNotaIntegrativaAllegata;
    rebuildFormArray(fb, notaIntegrativaAllegata, form, `${formPaths.bilancio}.my_NotaIntegrativaAllegata.my_ElNotaIntegrativaAllegata`, (fbInner, item) =>
        buildNotaIntegrativaAllegataGroup(fbInner, item)
    );
};

export const buildNotaIntegrativaAllegataGroup = (fb: FormBuilder, item?: MyElNotaIntegrativaAllegata): FormGroup => {
    return fb.group({
        my_NotaIntegrativa: item?.my_NotaIntegrativa ?? '',
    });
};

export const buildCopiaBilancioAllegatoFormArray = (fb: FormBuilder, myVerbale: MyVerbale, form: FormGroup) => {
    const copiaBilancioAllegato = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CopiaBilancioAllegato.my_ElCopiaBilancioAllegato;
    rebuildFormArray(fb, copiaBilancioAllegato, form, `${formPaths.bilancio}.my_CopiaBilancioAllegato.my_ElCopiaBilancioAllegato`, (fbInner, item) =>
        buildCopiaBilancioAllegatoGroup(fbInner, item)
    );
};

export const buildCopiaBilancioAllegatoGroup = (fb: FormBuilder, item?: MyElCopiaBilancioAllegato): FormGroup => {
    return fb.group({
        my_CopiaBilancio: item?.my_CopiaBilancio ?? '',
    });
};

export const buildCertificazioniPassateFormArray = (fb: FormBuilder, myVerbale: MyVerbale, form: FormGroup) => {
    const certificazioniPassate = myVerbale.my_CertificazioniPassate.my_ElCertificazioniPassate;
    rebuildFormArray(fb, certificazioniPassate, form, 'my_CertificazioniPassate.my_ElCertificazioniPassate', (fbInner, item) =>
        buildCertificazionePassataGroup(fbInner, item)
    );
};

export const buildCertificazionePassataGroup = (fb: FormBuilder, item?: MyElCertificazioniPassateEntity): FormGroup => {
    return fb.group({
        my_CertPassataAnnoBil: item?.my_CertPassataAnnoBil ?? '',
        my_CertPassataSocieta: item?.my_CertPassataSocieta ?? '',
        my_CertPassataIdSocieta: item?.my_CertPassataIdSocieta ?? '',
        my_CertPassataEsito: item?.my_CertPassataEsito ?? '',
        my_CertPassataIdCertificazione: item?.my_CertPassataIdCertificazione ?? '',
    });
};
